import Layer, { Result } from './Layer.js';

import { SensorDataManager } from '@data/Simulations/RawSensorDatas.js';
import { RaysLoadingStatus, SIM_NOISE, SIM_SUN, SIM_SUN4D, SIM_TARGET, SIM_VVA, SIM_WIND, SubSimulationJSON } from '@visualizer/types';
import Grid from 'Geodata/grid.js';
import { FileFetcherV2 } from 'Loader/FileFetcherv2.js';
import { BinaryReader } from 'Loader/serialization/common/BinaryReader.js';
import { getMultiLineJumper, MultiLineJumper } from 'Loader/serialization/jumper/MultiLineJumper.js';
import { getRaysJumper, RaysJumper } from 'Loader/serialization/jumper/RaysJumper.js';
import { LatestResultSchema } from 'Loader/serializerV2/ResultSerializer.js';
import { SimulationSet } from '../data/SimulationSet';
import { Frame } from './Frame.js';
import RaysHandler from './RaysHandler.js';
import usePersistentStore from '@gui/store/persistent/persistentStore';
import { getDefaultVectorialScalarMode } from './SimConfig';
import { ApiRoutes } from 'store/ApiRoutes';
type SimulationKinds = 0 | 1 | 2 | 4 | 8 | 16 | 32 | 64 | 128 | 256;

class LayerStandard extends Layer {
    name: string;

    seedFile: any;
    protected readonly simKind: SimulationKinds;

    constructor(id: string, parentSimulationSet: SimulationSet, subSimulation: SubSimulationJSON, results: LatestResultSchema[]) {
        super(parentSimulationSet, id);
        this.simKind = results[0].simKind as SimulationKinds;
        const currentMode = usePersistentStore.getState().layers.layerStates[this.getUID()]?.VECTORIAL_SCALAR_MODE;
        const mustForceModule = this.simKind === SIM_WIND || this.simKind === SIM_NOISE;
        if (mustForceModule && currentMode !== 0) {
            usePersistentStore.getState().updateLayerState(this.getUID(), { VECTORIAL_SCALAR_MODE: 0 });
        } else if (this.isNewLayerState) {
            usePersistentStore.getState().updateLayerState(this.getUID(), {
                VECTORIAL_SCALAR_MODE: getDefaultVectorialScalarMode(this.simKind),
            });
        }
        this.subSimulationId = subSimulation.id;
        this.name = subSimulation.name;

        this.parentSimulationSet = parentSimulationSet;

        for (const serializedResult of results) {
            const frameCollection = [];

            for (let f = 0; f < serializedResult.frameMax; f++) {
                const frameName = serializedResult.frameNames[f];
                const scalarValues = serializedResult.scalarResults[f];
                const vectorialValues = serializedResult.vectorResults[f];

                const sdmanager = new SensorDataManager(new Float32Array(scalarValues), vectorialValues);

                const actualFrame = new Frame(this, frameName, sdmanager);
                frameCollection.push(actualFrame);
            }

            const result: Result = {
                unitOfMeasure: serializedResult.unitOfMeasure,
                resultLabel: serializedResult.resultLabel,
                resultId: serializedResult.resultId,
                frameCollection,
            };
            this.results.push(result);
        }

        if (this.simKind === SIM_VVA && this.isNewLayerState) {
            const frameCount = this.getFrameCollectionById(0).length;
            const lastFrameIndex = Math.max(0, frameCount - 1);

            usePersistentStore.getState().updateLayerState(this.getUID(), {
                segments: {
                    frameMax: frameCount,
                    frameSelected: 0,
                    frameNames: this.getFrameCollectionById(0).map((frame) => frame.getName()),
                    isAggregate: true,
                    framesList: Array.from({ length: frameCount }, (_, index) => index),
                    aggregateRange: [0, lastFrameIndex],
                    aggregateWrap: false,
                },
            });
        }
    }

    unloadRays(setStatus: (status: number) => void) {
        console.log('Unloading rays');
        this.raysHandler?.clear();
        this.raysHandler = undefined;
        setStatus(RaysLoadingStatus.NotLoaded);
    }

    private getRaysAssetKind(): 'rays' | 'multilines' {
        return this.isOfType(SIM_WIND) || this.isOfType(SIM_NOISE) ? 'multilines' : 'rays';
    }

    public getRaysCacheKey(): string {
        return `${this.UID}/${this.getRaysAssetKind()}`;
    }

    public getRaysEndpoint(): string {
        return ApiRoutes.raysDownload(this.parentSimulationSet.id, this.subSimulationId, this.getRaysAssetKind());
    }

    public getSubSimulationId(): number {
        return this.subSimulationId;
    }

    public async downloadRays() {
        const fileFetcher = new FileFetcherV2();
        const cacheKey = this.getRaysCacheKey();
        const endpoint = this.getRaysEndpoint();

        await fileFetcher.downloadFile(cacheKey, endpoint);
        return await fileFetcher.getFile(cacheKey);
    }

    async loadItems(setStatus: (status: number) => void, type: 'rays' | 'ints', setErrorMessage?: (message: string) => void) {
        console.log(`Downloading ${type}`);

        setErrorMessage?.('');
        try {
            setStatus(RaysLoadingStatus.Initializing);
            const buffer = await this.downloadRays();

            console.log(`Loading ${type}`);
            setStatus(RaysLoadingStatus.Loading);
            const isMultiline = this.isOfType(SIM_WIND) || this.isOfType(SIM_NOISE);
            const isRays = this.isOfType(SIM_SUN) || this.isOfType(SIM_SUN4D) || this.isOfType(SIM_VVA) || this.isOfType(SIM_TARGET);

            let jumper: RaysJumper | MultiLineJumper | null = null;

            if (isRays) {
                jumper = await getRaysJumper(new BinaryReader(buffer));
            } else if (isMultiline) {
                jumper = await getMultiLineJumper(new BinaryReader(buffer));
            } else {
                throw new Error('No .rtt or .mlt file found in seedFile');
            }

            if (this.raysHandler) {
                this.raysHandler.clear();
            }
            this.raysHandler = new RaysHandler(this, jumper);
            setStatus(RaysLoadingStatus.Loaded);
        } catch (error) {
            console.error('Failed to load items:', error);
            const message = error instanceof Error ? error.message : 'Failed to load simulation items.';
            setErrorMessage?.(message);
            setStatus(RaysLoadingStatus.Error);
        }
    }

    updateRaysMesh(drawRays: boolean, sensorIDs: number[], mode: 'visible' | 'all') {
        this.disposeRays();

        if (!drawRays) return;

        this.raysHandler?.createRays(
            sensorIDs.filter((id) => id >= 0 && id < Grid.sensorPointsN),
            mode
        );
    }

    disposeRays() {
        this.raysHandler?.disposeRays();
    }

    getRaysHandler() {
        return this.raysHandler;
    }

    // -------------------
    // INTERSECTIONS CODE
    // -------------------

    disposeIntersections() {
        // if (this.intsHandler.intsShape) {
        //     getViewer().scene.remove(this.intsHandler.intsShape);
        // }
        // if (this.intsHandler.intsShape) {
        //     this.intsHandler.intsShape.traverse((obj) => {
        //         const mesh = obj as THREE.Mesh;
        //         if ((mesh as any).geometry) {
        //             mesh.geometry.dispose();
        //         }
        //         if ((mesh as any).material) {
        //             if (Array.isArray(mesh.material)) {
        //                 mesh.material.forEach((m) => m.dispose());
        //             } else {
        //                 mesh.material.dispose();
        //             }
        //         }
        //     });
        // }
        // this.intsHandler.intsShape = null;
    }

    updateIntsMesh(_drawInts: boolean, _sensorIDs: number[]) {
        // this.disposeIntersections();
        // if (!drawInts) return;
        // const sensorData = this.getActiveFrame().getSensors();
        // // Generate intersections only if Selected Sensor is defined and valid:
        // sensorIDs = sensorIDs.filter((id) => typeof id === 'number' && id >= 0).filter((id) => sensorData[id]?.isActive?.());
        // this.intsHandler.createIntersections(sensorIDs);
    }

    unloadIntersections(_setStatus: (status: number) => void) {
        // console.log('Unloading intersections');
        // this.intsHandler.clear();
        // setStatus(RaysLoadingStatus.NotLoaded);
    }
}

export default LayerStandard;
